import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, takeUntil } from 'rxjs/operators';

import {
  AddressCO,
  APIServerResponse,
  EnquiryType,
  SchoolCO,
  SourceType,
  VisitorCO,
  VisitorStatus
} from 'src/app/shared/models/ApiModels';

import { ApiService } from 'src/app/shared/services/api.service';
import { environment } from 'src/environments/environment';
import { SchoolDetailsService } from 'src/app/shared/services/school-details.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-enquiry-dialog',
  templateUrl: './enquiry-dialog.component.html',
  styleUrls: ['./enquiry-dialog.component.scss'],
  animations: [
    trigger('expand', [
      transition(':enter', [
        style({ transform: 'scale(0.9)', opacity: 0 }),
        animate('300ms cubic-bezier(.25,.8,.25,1)',
          style({ transform: 'scale(1)', opacity: 1 })
        )
      ]),
      transition(':leave', [
        animate('200ms ease-in',
          style({ transform: 'scale(0.9)', opacity: 0 })
        )
      ])
    ])
  ]
})
export class EnquiryDialogComponent implements OnInit {

  @Output() closes = new EventEmitter<void>();

  loading = false;

  enquiryTypes = EnquiryType;
  enquiryTypeKeys: (keyof typeof EnquiryType)[] = [];

  model: VisitorCO = {
    address: new AddressCO(),
    remarks: '',
    visitorName: '',
    phoneNo: '',
    emailId: '',
    admissionClass: '',
  } as VisitorCO;

  classOptions: string[] = [
    'Nursery','LKG','UKG','I','II','III','IV','V',
    'VI','VII','VIII','IX','X','XI','XII'
  ];

  constructor(
    private snackBar: MatSnackBar,
    private apiService: ApiService,
    private schoolService: SchoolDetailsService,
  ) {}

  ngOnInit(): void {
    this.enquiryTypeKeys = Object.keys(this.enquiryTypes)
      .filter(v => typeof v === 'string') as (keyof typeof EnquiryType)[];

    this.model.enquryType = EnquiryType.Admission;
    this.model.status = VisitorStatus.OPEN;
    
    this.model.source = SourceType.WEBSITE;
    this.subscribeToSchoolState();
  }

  
  private destroy$ = new Subject<void>();
  school: SchoolCO | null = null;

    ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
    }
  
    private subscribeToSchoolState(): void {
      this.schoolService.schoolState$
        .pipe(takeUntil(this.destroy$))
        .subscribe(state => {
          if (state.loading) {
            return;
          }
  
          if (state.error) {
            this.showError(state.error);
            return;
          }
  
          if (!state.data) {
            return;
          }
  
          this.school = state.data; 
          this.model.school = { id: this.school?.id } as SchoolCO;    
        });
    }
  onEnquiryTypeChange(): void {
    if (this.model.enquryType !== EnquiryType.Admission) {
      this.model.admissionClass = '';
    }
  }

  submit(form: NgForm): void {
    if (form.invalid) {
      form.control.markAllAsTouched();
      this.showError('Please fix the errors in the form.');
      return;
    }

    if (this.loading) return;

    this.loading = true;
    this.sanitizeModel();

    if (this.model.enquryType === EnquiryType.Admission &&
        !this.model.remarks?.startsWith('Admission for Class:')) {

      this.model.remarks =
        `Admission for Class: ${this.model.admissionClass}. ${this.model.remarks || ''}`;
    }

    const now = new Date();
    this.model.enquirydate = now.toLocaleDateString('en-GB');
    this.model.timeIN = now.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });

    console.log(this.model);
    
    this.apiService.createVisitorEnquiry(this.model)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: APIServerResponse) => {
          if (res?.statusCode == 200) {
            this.showSuccess('Enquiry submitted successfully.');
            this.closes.emit();
          } else {
            this.showError(res?.message || 'Failed to submit enquiry.');
          }
        },
        error: () => {
          this.showError('Server error. Please try again later.');
        }
      });
  }

  private sanitizeModel(): void {
    this.model.visitorName = this.model.visitorName?.trim();
    this.model.phoneNo = this.model.phoneNo?.trim();
    this.model.emailId = this.model.emailId?.trim();
    this.model.remarks = this.model.remarks?.trim();
  }

  trackByFn(_: number, item: keyof typeof EnquiryType): string {
    return item;
  }

  closeDialog(): void {
    this.closes.emit();
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: ['error-snackbar']
    });
  }

  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: ['success-snackbar']
    });
  }
}
